import { Select } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import { useRuntimeLocalization } from '@/lib/localizationContext'

/**
 * Each option is labelled in its OWN language (the autonym) — Arabic reads
 * "العربية", not "Arabic (Saudi Arabia)" spelled out in English. A reader who
 * cannot read the current language still has to be able to find theirs in
 * this list, which is the whole point of the control.
 *
 * The region is dropped unless it actually disambiguates. `Intl.DisplayNames`
 * on a full tag returns "العربية (المملكة العربية السعودية)" — 33 characters
 * of nowrap text in a header, to say nothing an en-SA/ar-SA pair needs said.
 * It comes back only when two offered locales share a language (en-GB/en-US),
 * where "English" twice would be useless.
 */
function languageLabel(locale: string, ambiguous: boolean): string {
	try {
		const labels = new Intl.DisplayNames([locale], { type: 'language' })
		const tag = ambiguous ? locale : locale.split('-')[0]
		return labels.of(tag) || labels.of(locale) || locale
	} catch {
		return locale
	}
}

/** Languages offered more than once, so only those keep their region. */
function ambiguousLanguages(locales: string[]): Set<string> {
	const seen = new Map<string, number>()
	for (const locale of locales) {
		const lang = locale.split('-')[0]
		seen.set(lang, (seen.get(lang) ?? 0) + 1)
	}
	return new Set(
		[...seen.entries()].filter(([, count]) => count > 1).map(([lang]) => lang)
	)
}

export interface LocaleSwitcherProps {
	visible?: boolean
	position?: 'left' | 'center' | 'right'
}

export default function LocaleSwitcher({ visible }: LocaleSwitcherProps = {}) {
	const router = useRouter()
	const t = useTranslations('nav')
	const localization = useRuntimeLocalization()
	const isVisible =
		visible !== undefined
			? visible
			: localization.language_switcher_visible !== false
	if (!isVisible) return null

	const locales = localization.supported_locales || []
	if (locales.length < 2) return null
	const ambiguous = ambiguousLanguages(locales)

	const changeLocale = async (target: string) => {
		const base = target.split('-')[0]
		const routeLocale = router.locales?.includes(base)
			? base
			: router.locale || router.defaultLocale
		await router.push(
			{
				pathname: router.pathname,
				query: { ...router.query, lang: target },
			},
			undefined,
			{ locale: routeLocale }
		)
	}

	return (
		<Select
			aria-label={t('language')}
			value={localization.locale}
			onChange={event => void changeLocale(event.target.value)}
			size='sm'
			width='auto'
			minW='105px'
			borderRadius='full'
			bg='whiteAlpha.900'
		>
			{locales.map(locale => (
				<option key={locale} value={locale}>
					{languageLabel(locale, ambiguous.has(locale.split('-')[0]))}
				</option>
			))}
		</Select>
	)
}
